import { useEffect, useState } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { useOptionalAuth, ROLE_MODERATOR } from '../../context/AuthContext';

/**
 * The cities the signed-in moderator holds an active mandate for, by slug.
 *
 * Asked once per page load and per account, and remembered at module level:
 * every card of the feed asks the same question, and mandates do not change
 * between two of them. A resident never asks — the list is simply empty.
 *
 * This only decides whether a "Modérer" button is offered. The API checks the
 * mandate again on every decision.
 */
let cached = { username: null, slugs: [] };
let inFlight = null;

export function resetModeratedCitiesCache() {
  cached = { username: null, slugs: [] };
  inFlight = null;
}

async function loadSlugs(username) {
  if (cached.username === username) {
    return cached.slugs;
  }

  if (!inFlight) {
    inFlight = (async () => {
      let slugs = [];

      try {
        const response = await apiCall('/moderator/mandates', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          slugs = (data.data || [])
            .filter((mandate) => 'ACTIVE' === mandate.status)
            .map((mandate) => mandate.city?.slug)
            .filter(Boolean);
        }
      } catch {
        // No button is a safe answer: the reports still reach the desk.
      }

      cached = { username, slugs };
      inFlight = null;

      return slugs;
    })();
  }

  return inFlight;
}

export function useModeratedCities() {
  const { user } = useOptionalAuth();
  const isModerator = Array.isArray(user?.roles) && user.roles.includes(ROLE_MODERATOR);
  const username = isModerator ? user.username : null;
  const [slugs, setSlugs] = useState(
    username && cached.username === username ? cached.slugs : []
  );

  useEffect(() => {
    if (!username) {
      setSlugs([]);
      return undefined;
    }

    let cancelled = false;

    loadSlugs(username).then((loaded) => {
      if (!cancelled) {
        setSlugs(loaded);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [username]);

  return slugs;
}
